import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { C, SidebarIcons, ROLES } from '../constants';
import { menuPara } from '../modulos';
import { InnovaLogo } from './UI';
import { updatePassword } from '../supabase';
import { REGLA_CLAVE, validarClave, traducirErrorAuth } from '../utils/claveAuth';
import { CapaAtras } from '../utils/capas';
import ModalDosPasos from './ModalDosPasos';

// Roles a los que se ofrece la verificación en dos pasos (Lote B2).
const ROLES_DOS_PASOS = ['admin', 'qb'];

// Ícono "+" para el CTA de nueva cita (médico)
// Acento sobre el azul del menú (#152643): el #4A8CF0 de la web aclarado,
// porque el original no se lee bien como texto sobre fondo oscuro.
const LUZ = '#8DB8FF';
const CLAVE_PLEGADAS = 'il-menu-secciones-plegadas';

const Icono = ({ nombre, size = 16, style }) => {
  const I = SidebarIcons[nombre];
  return I ? <I size={size} style={{ flexShrink:0, ...style }} /> : null;
};

const PlusIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export default function Sidebar({ user, activeModule, onNavigate, onNuevaCita, onLogout, badges, permisos = {}, modulos = {}, consultaActiva = null, onIrAConsulta, onCerrarConsulta, esMovil = false, abierto = false, onCerrar }) {
  const [colapsadoEscritorio, setCollapsed] = useState(false);
  // En el teléfono el menú es un panel que se desliza sobre la pantalla: nunca
  // se colapsa a íconos, y cualquier elección lo cierra para dejar ver el módulo.
  const collapsed = esMovil ? false : colapsadoEscritorio;
  const cerrarSiMovil = () => { if (esMovil && onCerrar) onCerrar(); };

  // Secciones plegables (24-oct-2026): con todos los módulos abiertos el menú
  // del laboratorio satura. Cada quien pliega lo que no usa y el navegador lo
  // recuerda. La sección del módulo activo siempre se muestra abierta.
  const [plegadas, setPlegadas] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CLAVE_PLEGADAS) || '{}') || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(CLAVE_PLEGADAS, JSON.stringify(plegadas)); } catch { /* sin almacenamiento: solo dura la sesión */ }
  }, [plegadas]);
  const alternarSeccion = (id) => setPlegadas(p => ({ ...p, [id]: !p[id] }));
  const role     = user?.role || 'op';
  const roleInfo = ROLES[role] || ROLES.op;
  const isMedico = role === 'medico';
  // Un poco más ancho para que respiren los subtítulos y los targets táctiles.
  const W = esMovil ? 280 : (collapsed ? 64 : 240);

  // Cambio de contraseña (self-service)
  const [showPw,   setShowPw]   = useState(false);
  const [showDosPasos, setShowDosPasos] = useState(false);
  const [pw1,      setPw1]      = useState('');
  const [pw2,      setPw2]      = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwErr,    setPwErr]    = useState('');
  const [pwOk,     setPwOk]     = useState(false);

  const initials = (user?.name || user?.username || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const getBadge = (key) => (key && badges ? badges[key] || null : null);

  // Qué entradas se ofrecen lo decide el registro de módulos (src/modulos.js):
  // rol, módulo de la clínica y permiso, con la misma función que usa App.js
  // para decidir qué se pinta. Así el menú nunca ofrece una puerta que no abre.

  const abrirPw = () => { setPwErr(''); setPwOk(false); setPw1(''); setPw2(''); setShowPw(true); };
  const cerrarPw = () => { if (!pwSaving) setShowPw(false); };

  const cambiarClave = async () => {
    setPwErr('');
    const falta = validarClave(pw1);
    if (falta)          { setPwErr(falta); return; }
    if (pw1 !== pw2)    { setPwErr('Las contraseñas no coinciden.'); return; }
    setPwSaving(true);
    try {
      // updatePassword no lanza: devuelve { error }. Antes se ignoraba y el
      // menú decía «actualizada» aunque Auth la hubiera rechazado.
      const { error } = await updatePassword(pw1, { clearMustChange: true });
      if (error) { setPwErr(traducirErrorAuth(error, 'No se pudo actualizar la contraseña.')); return; }
      setPwOk(true);
      setPw1(''); setPw2('');
    } catch (e) {
      setPwErr(traducirErrorAuth(e, 'No se pudo actualizar la contraseña.'));
    } finally {
      setPwSaving(false);
    }
  };

  const inpStyle = {
    width:'100%', boxSizing:'border-box', padding:'9px 11px', marginTop:4,
    border:`1px solid ${C.border}`, borderRadius:8, fontSize:13,
    fontFamily:"'Inter','Segoe UI',sans-serif", color:C.navy, outline:'none',
  };

  const estiloPanel = esMovil
    ? {
        position:'fixed', top:0, bottom:0, left:0,
        width:W, maxWidth:'86vw',
        transform: abierto ? 'translateX(0)' : 'translateX(-105%)',
        transition:'transform 0.22s ease, visibility 0.22s',
        zIndex:1500,
        boxShadow: abierto ? '4px 0 24px rgba(0,0,0,0.35)' : 'none',
        paddingTop:'env(safe-area-inset-top)',
        paddingBottom:'env(safe-area-inset-bottom)',
      }
    : {
        width:W, minWidth:W, maxWidth:W,
        height:'100vh', position:'sticky', top:0,
        transition:'width 0.2s ease, min-width 0.2s ease',
        zIndex:100,
        boxShadow:'2px 0 12px rgba(0,0,0,0.25)',
      };

  return (
    <>
    {esMovil && abierto && (
      <div onClick={onCerrar} aria-hidden="true"
        style={{ position:'fixed', top:0, right:0, bottom:0, left:0, background:'rgba(13,43,69,0.5)', zIndex:1400 }} />
    )}
    <nav aria-hidden={esMovil && !abierto ? true : undefined} style={{
      ...estiloPanel,
      background:C.navy,
      display:'flex', flexDirection:'column',
      borderRight:'1px solid rgba(255,255,255,0.08)',
      overflow:'hidden',
      visibility: esMovil && !abierto ? 'hidden' : 'visible',
    }}>

      {/* Brand */}
      <div style={{ padding:collapsed?'14px 0':'15px 16px', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:collapsed?'center':'space-between', gap:8, flexShrink:0 }}>
        {!collapsed && <InnovaLogo height={28} white={true} />}
        {collapsed && (
          <div style={{ width:34, height:34, borderRadius:8, background:'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {/* Molinete del logo, en blanco */}
            <svg width="20" height="20" viewBox="181 564 1195 1195" aria-hidden="true">
              <path fill="#ffffff" d="M 1376.027344 564.519531 L 1376.027344 1161.722656 L 778.726562 564.519531 Z M 181.71875 564.519531 L 778.726562 1161.722656 L 778.726562 564.519531 Z M 1376.027344 1758.828125 L 1376.027344 1161.722656 L 778.726562 1161.722656 Z M 778.726562 1758.828125 L 778.726562 1161.722656 L 181.71875 1161.722656 Z"/>
            </svg>
          </div>
        )}
        <button
          onClick={() => (esMovil ? onCerrar && onCerrar() : setCollapsed(c => !c))}
          aria-label={esMovil ? 'Cerrar menú' : collapsed ? 'Expandir menú' : 'Colapsar menú'}
          style={{ background:'rgba(255,255,255,0.06)', border:'none', cursor:'pointer', color:'#B4C6E6', fontSize:18, lineHeight:1, width:esMovil?40:30, height:esMovil?40:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
        >
          {esMovil ? '✕' : collapsed ? '›' : '‹'}
        </button>
      </div>

      {/* Usuario */}
      {!collapsed ? (
        <div style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,0.08)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,0.05)', borderRadius:11, padding:'9px 10px' }}>
            <div style={{ width:38, height:38, borderRadius:'50%', background:C.teal, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:'700', color:C.navy, flexShrink:0 }}>
              {initials}
            </div>
            <div style={{ overflow:'hidden' }}>
              <div style={{ fontSize:13, fontWeight:'600', color:'#e8f4f8', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {user?.name || user?.username}
              </div>
              <div style={{ fontSize:10, color:LUZ, letterSpacing:1, fontFamily:"'Courier New',monospace", fontWeight:'600' }}>
                {roleInfo.label.toUpperCase()}{isMedico && user?.no_colegiado ? ` · ${user.no_colegiado}` : ''}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', justifyContent:'center', flexShrink:0 }}>
          <div style={{ width:36, height:36, borderRadius:'50%', background:C.teal, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:'700', color:C.navy }} title={user?.name || user?.username}>
            {initials}
          </div>
        </div>
      )}

      {/* CTA Nueva cita — solo médico */}
      {isMedico && (
        <div style={{ padding:collapsed?'10px 0':'12px 14px', flexShrink:0 }}>
          <button
            onClick={() => { cerrarSiMovil(); if (onNuevaCita) onNuevaCita(); else onNavigate('medico-clinica'); }}
            title="Nueva cita"
            style={{
              width:'100%', display:'flex', alignItems:'center', justifyContent:'center',
              gap:collapsed?0:8, padding:collapsed?'10px 0':'11px 12px',
              background:C.teal, color:'#fff', border:'none', borderRadius:10,
              cursor:'pointer', fontSize:13, fontWeight:'700',
              fontFamily:"'Inter','Segoe UI',sans-serif",
              boxShadow:'0 2px 8px rgba(74,140,240,0.35)',
            }}
            onMouseEnter={e=>{e.currentTarget.style.filter='brightness(1.08)';}}
            onMouseLeave={e=>{e.currentTarget.style.filter='none';}}
          >
            <PlusIcon size={collapsed?20:17} />
            {!collapsed && <span>Nueva cita</span>}
          </button>
        </div>
      )}

      {/* ── Consulta en curso ────────────────────────────────────────────────
          Mientras hay un expediente abierto, este acceso vive por encima del
          menú: el médico puede irse a Ultrasonidos, Resultados o su agenda y
          volver de un clic al mismo expediente, sin perder lo que escribió.
          La X es la única salida que cierra la consulta. */}
      {isMedico && consultaActiva && (
        <div style={{ padding: collapsed ? '0 0 10px' : '0 14px 12px', flexShrink: 0 }}>
          <div style={{
            display:'flex', alignItems:'center', gap: collapsed ? 0 : 8,
            justifyContent: collapsed ? 'center' : 'flex-start',
            background: activeModule === 'medico-workspace' ? 'rgba(74,140,240,0.22)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${activeModule === 'medico-workspace' ? C.teal : 'rgba(255,255,255,0.12)'}`,
            borderRadius: 10, padding: collapsed ? '8px 0' : '8px 10px',
          }}>
            <button
              onClick={() => { cerrarSiMovil(); if (onIrAConsulta) onIrAConsulta(); }}
              title={`Volver a la consulta${consultaActiva.nombre ? ' de ' + consultaActiva.nombre : ''}`}
              style={{
                flex:1, minWidth:0, display:'flex', alignItems:'center',
                gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start',
                background:'transparent', border:'none', cursor:'pointer', padding:0,
                color:'#e8f4f8', textAlign:'left', fontFamily:"'Inter','Segoe UI',sans-serif",
              }}
            >
              <Icono nombre="estetoscopio" size={17} style={{ color:LUZ }} />
              {!collapsed && (
                <span style={{ minWidth:0, overflow:'hidden' }}>
                  <span style={{ display:'block', fontSize:9, letterSpacing:1.4, color:LUZ, fontWeight:'700' }}>CONSULTA EN CURSO</span>
                  <span style={{ display:'block', fontSize:12, fontWeight:'600', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {consultaActiva.nombre || 'Expediente abierto'}
                  </span>
                </span>
              )}
            </button>
            {!collapsed && (
              <button
                onClick={() => { cerrarSiMovil(); if (onCerrarConsulta) onCerrarConsulta(); }}
                title="Cerrar la consulta y volver a Mis Pacientes"
                style={{
                  background:'transparent', border:'none', cursor:'pointer', flexShrink:0,
                  color:'#B4C6E6', fontSize:15, lineHeight:1, padding:'2px 4px',
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navegación */}
      <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', paddingBottom:8 }}>
        {(() => {
          const secciones = menuPara(user, modulos, permisos);
          // Con una sola sección (médico, secretaria…) plegar no tiene sentido.
          const plegable = secciones.length > 1 && !collapsed;
          return secciones.map(({ section, visibleItems }, idx) => {
          const tieneActivo = visibleItems.some(it => it.id === activeModule);
          const abierta = !plegable || tieneActivo || !plegadas[section.id];
          return (
            <div key={section.id} style={{
              marginTop: idx === 0 ? 4 : 8,
              paddingTop: idx === 0 ? 0 : 6,
              borderTop: idx === 0 ? 'none' : '1px solid rgba(255,255,255,0.09)',
            }}>
              {!collapsed
                ? (
                  <button
                    type="button"
                    onClick={plegable && !tieneActivo ? () => alternarSeccion(section.id) : undefined}
                    aria-expanded={abierta}
                    title={plegable && !tieneActivo ? (abierta ? 'Plegar sección' : 'Mostrar sección') : undefined}
                    style={{
                      width:'calc(100% - 16px)', margin:'2px 8px 4px', padding:'7px 8px',
                      display:'flex', alignItems:'center', gap:8,
                      background:'rgba(255,255,255,0.045)', border:'none', borderRadius:7,
                      cursor: plegable && !tieneActivo ? 'pointer' : 'default',
                      color:'#C9D6EE', fontFamily:"'Inter','Segoe UI',sans-serif",
                    }}
                  >
                    <span style={{ width:3, height:12, borderRadius:2, background:C.teal, flexShrink:0 }} />
                    <span style={{ flex:1, textAlign:'left', fontSize:10, letterSpacing:1.6, fontWeight:'800', textTransform:'uppercase' }}>{section.label}</span>
                    {plegable && !abierta && (
                      <span style={{ fontSize:10, fontWeight:'700', color:'#8AA3CC' }}>{visibleItems.length}</span>
                    )}
                    {plegable && !tieneActivo && (
                      <span aria-hidden="true" style={{ fontSize:11, color:'#8AA3CC', transform: abierta ? 'rotate(90deg)' : 'none', transition:'transform 0.15s' }}>›</span>
                    )}
                  </button>
                )
                : <div style={{ height:1, background:'rgba(255,255,255,0.12)', margin:'6px 10px' }} />
              }
              {abierta && visibleItems.map(item => {
                const isActive = activeModule === item.id;
                const badge    = getBadge(item.badge);
                return (
                  <button
                    key={item.id}
                    onClick={() => { cerrarSiMovil(); onNavigate(item.id); }}
                    title={collapsed ? item.label : undefined}
                    style={{
                      width:'100%', display:'flex', alignItems:'center',
                      gap:collapsed?0:11, justifyContent:collapsed?'center':'flex-start',
                      padding:collapsed?'11px 0':'10px 14px', margin:'1px 0',
                      minHeight:collapsed?44:46,
                      background:isActive?'rgba(74,140,240,0.18)':'transparent',
                      border:'none',
                      borderLeft:isActive?`3px solid ${C.teal}`:'3px solid transparent',
                      borderRadius:collapsed?0:'0 9px 9px 0',
                      cursor:'pointer',
                      color:isActive?LUZ:'#B4C6E6',
                      fontFamily:"'Inter','Segoe UI',sans-serif",
                      textAlign:'left', whiteSpace:'nowrap', position:'relative',
                    }}
                    onMouseEnter={e => { if(!isActive){e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.color='#e8f4f8';}}}
                    onMouseLeave={e => { if(!isActive){e.currentTarget.style.background='transparent';e.currentTarget.style.color='#B4C6E6';}}}
                  >
                    {/* Ícono SVG monocromático o emoji */}
                    {(() => {
                      const Icon = SidebarIcons[item.icon];
                      return Icon
                        ? <Icon size={18} style={{ flexShrink:0, opacity: isActive ? 1 : 0.75 }} />
                        : <span style={{ fontSize:17, flexShrink:0, lineHeight:1 }}>{item.icon}</span>;
                    })()}
                    {!collapsed && (
                      <span style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                        <span style={{ display:'block', fontSize:13.5, fontWeight:isActive?'700':'600', overflow:'hidden', textOverflow:'ellipsis' }}>{item.label}</span>
                        {item.sub && (
                          <span style={{ display:'block', fontSize:10.5, fontWeight:'500', color:isActive?'rgba(141,184,255,0.85)':'#8AA3CC', overflow:'hidden', textOverflow:'ellipsis', marginTop:1 }}>{item.sub}</span>
                        )}
                      </span>
                    )}
                    {/* Badge numérico (notificaciones) */}
                    {badge && !collapsed && (
                      <span style={{ background:item.badge==='pending_results'?'#f97316':C.teal, color:item.badge==='pending_results'?'#fff':C.navy, fontSize:9, fontWeight:'700', padding:'2px 6px', borderRadius:9, minWidth:18, textAlign:'center', flexShrink:0 }}>
                        {badge}
                      </span>
                    )}
                    {badge && collapsed && (
                      <span style={{ position:'absolute', top:7, right:9, width:7, height:7, borderRadius:'50%', background:'#f97316' }} />
                    )}
                    {/* Badge 'propio' — solo en expanded */}
                    {item.badge_label && !collapsed && !badge && (
                      <span style={{ fontSize:8, fontWeight:'700', padding:'2px 6px', borderRadius:8, background:'rgba(74,140,240,0.15)', color:LUZ, border:`1px solid ${C.teal}40`, flexShrink:0, letterSpacing:0.5 }}>
                        {item.badge_label}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
          });
        })()}
      </div>

      {/* Footer - cambiar contraseña + cerrar sesión */}
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', padding:collapsed?'8px 0':'8px', flexShrink:0, display:'flex', flexDirection:'column', gap:2 }}>
        {/* Cambiar contraseña */}
        <button
          onClick={() => { cerrarSiMovil(); abrirPw(); }}
          title="Cambiar mi contraseña"
          style={{ width:'100%', display:'flex', alignItems:'center', gap:collapsed?0:10, justifyContent:collapsed?'center':'flex-start', padding:collapsed?'10px 0':'9px 12px', minHeight:42, background:'none', border:'none', borderRadius:9, cursor:'pointer', color:'#B4C6E6', fontSize:12, fontFamily:"'Inter','Segoe UI',sans-serif", opacity:0.9 }}
          onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.color='#e8f4f8';e.currentTarget.style.opacity='1';}}
          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#B4C6E6';e.currentTarget.style.opacity='0.9';}}
        >
          <Icono nombre="candado" size={16} />
          {!collapsed && <span>Cambiar contraseña</span>}
        </button>

        {/* Verificación en dos pasos (admin y qb) */}
        {ROLES_DOS_PASOS.includes(role) && (
          <button
            onClick={() => { cerrarSiMovil(); setShowDosPasos(true); }}
            title="Verificación en dos pasos"
            style={{ width:'100%', display:'flex', alignItems:'center', gap:collapsed?0:10, justifyContent:collapsed?'center':'flex-start', padding:collapsed?'10px 0':'9px 12px', minHeight:42, background:'none', border:'none', borderRadius:9, cursor:'pointer', color:'#B4C6E6', fontSize:12, fontFamily:"'Inter','Segoe UI',sans-serif", opacity:0.9 }}
            onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.color='#e8f4f8';e.currentTarget.style.opacity='1';}}
            onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#B4C6E6';e.currentTarget.style.opacity='0.9';}}
          >
            <Icono nombre="candado" size={16} />
            {!collapsed && <span>Verificación en dos pasos</span>}
          </button>
        )}

        {/* Cerrar sesión */}
        <button
          onClick={onLogout}
          title="Cerrar sesión"
          style={{ width:'100%', display:'flex', alignItems:'center', gap:collapsed?0:10, justifyContent:collapsed?'center':'flex-start', padding:collapsed?'10px 0':'9px 12px', minHeight:42, background:'none', border:'none', borderRadius:9, cursor:'pointer', color:'#dc2626', fontSize:12, fontFamily:"'Inter','Segoe UI',sans-serif", opacity:0.85 }}
          onMouseEnter={e=>{e.currentTarget.style.background='rgba(220,38,38,0.1)';e.currentTarget.style.opacity='1';}}
          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.opacity='0.85';}}
        >
          <Icono nombre="salir" size={16} />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>

      {showDosPasos && <ModalDosPasos onCerrar={() => setShowDosPasos(false)} />}

      {/* Modal: Cambiar mi contraseña */}
      {showPw && createPortal(
        <div
          onClick={cerrarPw}
          style={{ position:'fixed', inset:0, background:'rgba(13,43,69,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:16 }}
        >
          <CapaAtras onCerrar={cerrarPw} />
          <div
            onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:14, padding:'22px 22px 20px', width:'100%', maxWidth:380, boxShadow:'0 20px 60px rgba(0,0,0,0.3)', fontFamily:"'Inter','Segoe UI',sans-serif" }}
          >
            <div style={{ fontSize:15, fontWeight:'700', color:C.navy, marginBottom:4 }}>Cambiar mi contraseña</div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:16 }}>{REGLA_CLAVE} Se aplica a tu sesión actual.</div>

            {pwOk ? (
              <div style={{ padding:'12px 14px', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.35)', borderRadius:8, fontSize:13, color:'#047857', fontWeight:'600' }}>
                ✓ Contraseña actualizada correctamente.
              </div>
            ) : (
              <>
                <label style={{ fontSize:11, fontWeight:'600', color:C.navy }}>Nueva contraseña</label>
                <input
                  type="password"
                  value={pw1}
                  onChange={e => setPw1(e.target.value)}
                  autoFocus
                  style={{ ...inpStyle, marginBottom:12 }}
                  placeholder="••••••••"
                />
                <label style={{ fontSize:11, fontWeight:'600', color:C.navy }}>Confirmar contraseña</label>
                <input
                  type="password"
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !pwSaving) cambiarClave(); }}
                  style={inpStyle}
                  placeholder="••••••••"
                />
                {pwErr && (
                  <div style={{ color:C.red, fontSize:11, marginTop:10 }}>{pwErr}</div>
                )}
              </>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:18 }}>
              {pwOk ? (
                <button
                  onClick={() => setShowPw(false)}
                  style={{ padding:'8px 16px', border:'none', borderRadius:8, cursor:'pointer', background:C.teal, color:'#fff', fontSize:12, fontWeight:'700' }}
                >
                  Cerrar
                </button>
              ) : (
                <>
                  <button
                    onClick={cerrarPw}
                    disabled={pwSaving}
                    style={{ padding:'8px 16px', border:`1px solid ${C.border}`, borderRadius:8, cursor:pwSaving?'default':'pointer', background:'#fff', color:C.muted, fontSize:12, fontWeight:'600', opacity:pwSaving?0.6:1 }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={cambiarClave}
                    disabled={pwSaving}
                    style={{ padding:'8px 16px', border:'none', borderRadius:8, cursor:pwSaving?'default':'pointer', background:C.teal, color:'#fff', fontSize:12, fontWeight:'700', opacity:pwSaving?0.7:1 }}
                  >
                    {pwSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </nav>
    </>
  );
}
